import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, useMotionValue } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import pandaAvatar from "../../../../../assets/PandaProfile.png";
import { progressService } from "../../../../../services/progressService";

/* Animal icons */
import Lion from "../../../../../assets/animals/Lion.png";
import Monkey from "../../../../../assets/animals/Monkey.png";
import Tiger from "../../../../../assets/animals/Tiger.png";
import Koala from "../../../../../assets/animals/Koala.png";
import Fox from "../../../../../assets/animals/Fox.png";
import Panda from "../../../../../assets/animals/Piglet.png";
import Cat from "../../../../../assets/animals/Cat.png";
import Gorilla from "../../../../../assets/animals/Gorilla.png";
import Hyena from "../../../../../assets/animals/Hyena.png";

const animalIcons = [Lion, Monkey, Tiger, Koala, Fox, Panda, Cat, Gorilla, Hyena];

/* ---------------- Portal ---------------- */
const DragPortal = ({ children, isDragging }) => {
  if (!isDragging) return children;
  const root = document.getElementById("drag-layer") || document.body;
  return ReactDOM.createPortal(children, root);
};

/* ---------------- Compact Draggable Icon ---------------- */
const CompactDraggableIcon = ({ stakeholder, onDrop, index = 0 }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const ref = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [grabOffset, setGrabOffset] = useState({ x: 0, y: 0 });
  const [fixedPos, setFixedPos] = useState({ top: 0, left: 0 });
  const label = stakeholder.relation
    ? `${stakeholder.name} — ${stakeholder.relation}`
    : stakeholder.name;

  const handleDragStart = (e) => {
    setIsDragging(true);
    const rect = ref.current.getBoundingClientRect();
    setGrabOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setFixedPos({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleDragEnd = (e, info) => {
    setIsDragging(false);
    x.set(0);
    y.set(0);
    onDrop(stakeholder, info.point);
  };

  const animal = stakeholder.animal || animalIcons[index % animalIcons.length];

  return (
    <DragPortal isDragging={isDragging}>
      <motion.div
        ref={ref}
        drag
        dragElastic={0.05}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        title={label}
        aria-label={label}
        style={{
          x,
          y,
          position: isDragging ? "fixed" : "relative",
          top: isDragging ? `${fixedPos.top - grabOffset.y}px` : undefined,
          left: isDragging ? `${fixedPos.left - grabOffset.x}px` : undefined,
          zIndex: isDragging ? 9999 : "auto",
          cursor: isDragging ? "grabbing" : "grab",
        }}
        whileDrag={{ scale: 1.05 }}
        className="w-[60px] h-[60px] flex items-center justify-center bg-white border border-[#E0E0E0] rounded-full shadow-sm"
      >
        <img
          src={animal}
          alt={stakeholder.name}
          draggable={false}
          className="w-10 h-10 object-contain pointer-events-none select-none"
        />
      </motion.div>
    </DragPortal>
  );
};

/* ---------------- Draggable Card ---------------- */
const DraggableCard = ({ stakeholder, onDrop, index = 0 }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const ref = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [grabOffset, setGrabOffset] = useState({ x: 0, y: 0 });
  const [fixedPos, setFixedPos] = useState({ top: 0, left: 0 });

  const handleDragStart = (e) => {
    setIsDragging(true);
    const rect = ref.current.getBoundingClientRect();
    setGrabOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setFixedPos({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleDragEnd = (e, info) => {
    setIsDragging(false);
    x.set(0);
    y.set(0);
    onDrop(stakeholder, info.point);
  };

  const animal =
    stakeholder.animal || animalIcons[index % animalIcons.length];

  return (
    <DragPortal isDragging={isDragging}>
      <motion.div
        ref={ref}
        drag
        dragElastic={0.05}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={{
          x,
          y,
          position: isDragging ? "fixed" : "relative",
          top: isDragging ? `${fixedPos.top - grabOffset.y}px` : undefined,
          left: isDragging ? `${fixedPos.left - grabOffset.x}px` : undefined,
          zIndex: isDragging ? 9999 : "auto",
          cursor: isDragging ? "grabbing" : "grab",
        }}
        whileDrag={{ scale: 1.05, boxShadow: "0 8px 16px rgba(0,0,0,0.15)" }}
        className="bg-white border border-[#E0E0E0] rounded-xl px-3 py-2 flex items-center gap-2 shadow-sm select-none"
      >
        {/* circular avatar holder */}
        <div className="w-10 h-10 flex items-center justify-center bg-white border border-[#E0E0E0] rounded-full flex-shrink-0">
          <img
            src={animal}
            alt="Animal"
            draggable={false}
            className="w-6 h-6 object-contain pointer-events-none select-none"
          />
        </div>

        {/* stakeholder name and relation */}
        <div className="flex flex-col leading-tight">
          <span className="text-[#1C1C1C] font-semibold text-[14px]">
            {stakeholder.name}
          </span>
          <span className="text-[#7A7A7A] text-[12px] tracking-wide uppercase">
            {stakeholder.relation}
          </span>
        </div>
      </motion.div>
    </DragPortal>
  );
};


/* ---------------- Main Component ---------------- */
const StakeholderVisibilityMap = ({
  userId,
  subsectionId,
  onNextStep,
  onPreviousStep,
  onDataChange,
}) => {
  const dropZones = useRef({});
  const [columns, setColumns] = useState(null);
  const [loading, setLoading] = useState(true);

  const makeQuadrants = (list = []) => ({
    unassigned: { title: "Your Stakeholders", items: list },
    q1: { title: "High Influence, Low visibility*", color: "#ED4E4E", items: [] },
    q2: { title: "High Influence, High visibility*", color: "#ED8A19", items: [] },
    q3: { title: "Low Influence, Low visibility*", color: "#4BAF50", items: [] },
    q4: { title: "Low Influence, High visibility*", color: "#4696EC", items: [] },
  });

  useEffect(() => {
  async function fetchData() {
    try {
      setLoading(true);

      // 1️⃣ Get latest stakeholder list from backend
      const userData = await progressService.getUserDataForSection(
        userId,
        "course_career_progression",
        "interpersonal",
        "interpersonal_list"
      );
      const latestStakeholders = (userData?.stakeholders || []).map((s, i) => ({
        ...s,
        animal: s.animal || animalIcons[i % animalIcons.length],
      }));

      // 2️⃣ Get saved visibility map
      const saved = await progressService.getUserProgress(userId, subsectionId);
      const savedMap = saved?.data?.visibilityMap;

      // 3️⃣ Sync differences
      const syncStakeholders = (map, latestList) => {
        if (!map || Object.keys(map).length === 0) return makeQuadrants(latestList);

        const allInMap = Object.values(map).flatMap((col) => col.items);
        const latestIds = new Set(latestList.map((s) => s.id));
        const mapIds = new Set(allInMap.map((s) => s.id));

        const synced = { ...map };

        // 🧹 Remove deleted stakeholders
        for (const key of Object.keys(synced)) {
          synced[key].items = synced[key].items.filter((s) => latestIds.has(s.id));
        }

        // ➕ Add new stakeholders to unassigned
        const missing = latestList.filter((s) => !mapIds.has(s.id));
        if (missing.length > 0) {
          synced.unassigned.items = [...(synced.unassigned.items || []), ...missing];
        }

        // 🔄 Update existing stakeholder details (name, relation, animal)
        for (const key of Object.keys(synced)) {
          synced[key].items = synced[key].items.map((s) => {
            const updated = latestList.find((ls) => ls.id === s.id);
            return updated ? { ...s, ...updated } : s;
          });
        }

        return synced;
      };

      const syncedMap = syncStakeholders(savedMap, latestStakeholders);

      // 4️⃣ If map changed, persist back to backend
      const isDifferent =
        JSON.stringify(savedMap || {}) !== JSON.stringify(syncedMap);

      if (isDifferent) {
        await progressService.saveProgress(
          userId,
          subsectionId,
          { visibilityMap: syncedMap },
          "in_progress"
        );
      }

      setColumns(syncedMap);
    } catch (err) {
      console.error("Failed to load stakeholder visibility map:", err);
      setColumns(makeQuadrants([]));
    } finally {
      setLoading(false);
    }
  }

  if (userId && subsectionId) fetchData();
}, [userId, subsectionId]);


  const handleDrop = (stakeholder, point) => {
    for (const [id, ref] of Object.entries(dropZones.current)) {
      if (!ref) continue;
      const rect = ref.getBoundingClientRect();
      if (
        point.x >= rect.left &&
        point.x <= rect.right &&
        point.y >= rect.top &&
        point.y <= rect.bottom
      ) {
        moveStakeholder(stakeholder, id);
        return;
      }
    }
    moveStakeholder(stakeholder, "unassigned");
  };

  const moveStakeholder = (stakeholder, targetId) => {
    setColumns((prev) => {
      if (!prev) return prev;
      const newCols = Object.entries(prev).reduce((acc, [key, col]) => {
        acc[key] = {
          ...col,
          items: col.items.filter((s) => s.id !== stakeholder.id),
        };
        return acc;
      }, {});
      newCols[targetId].items = [...newCols[targetId].items, stakeholder];
      progressService
        .saveProgress(userId, subsectionId, { visibilityMap: newCols }, "in_progress")
        .catch((err) => console.error("Error saving stakeholder map:", err));
      onDataChange?.({ visibilityMap: newCols });
      return newCols;
    });
  };

  if (loading || !columns) {
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-500">
        Loading stakeholder data...
      </div>
    );
  }

  const total = Object.values(columns).reduce(
    (acc, col) => acc + col.items.length,
    0
  );
  const mapComplete = total > 0 && columns.unassigned.items.length === 0;

  const handleNext = async () => {
    if (!columns) return;
    const status = mapComplete ? "completed" : "in_progress";
    try {
      await progressService.saveProgress(
        userId,
        subsectionId,
        { visibilityMap: columns },
        status
      );
      onDataChange?.({ visibilityMap: columns });
    } catch (err) {
      console.error("Error saving stakeholder map:", err);
    } finally {
      onNextStep?.();
    }
  };

  return (
<motion.div
  className="relative w-full max-w-6xl mx-auto bg-white"
  initial={{ opacity: 0, y: 15 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.3 }}
>
  {/* === PANDA + CONTENT GRID === */}
  <div className="grid grid-cols-[64px_1fr] gap-4 items-start">
    {/* 🐼 Panda column */}
        <div className="w-[72px] h-[72px] rounded-full bg-[#5EB1BF] flex items-center justify-center overflow-hidden">
          <img
            src={pandaAvatar}
            alt="Panda"
            className="w-[72px] h-[72px] object-contain"
          />
        </div>

    {/* === TEXT + MAIN CONTENT COLUMN === */}
    <div className="flex flex-col gap-5 pb-4">
      {/* Header */}
      <div>
        <h3 className="text-[22px] font-gotham font-semibold text-[#1C1C1C] leading-tight">
          Let’s map your stakeholders based on their Visibility and Influence
        </h3>
        <p className="text-[15px] font-gotham font-normal text-[#7A7A7A] mt-1">
          Drag and drop your stakeholders’ visibility
        </p>
      </div>

      {/* Main content: left panel + right grid */}
      <div className="flex flex-col md:flex-row gap-5">
        {/* Left panel */}
        <div className="md:w-[36%] w-full bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl p-4 shadow-sm">
          <h4 className="font-semibold font-gotham text-[15px] text-[#1C1C1C] mb-4 tracking-tight">
            YOUR STAKEHOLDERS
          </h4>
          <div className="flex flex-col gap-1">
            {columns.unassigned.items.length === 0 ? (
              <p className="text-sm text-gray-400 font-gotham font-normal">
                No unassigned stakeholders.
              </p>
            ) : (
              columns.unassigned.items.map((s, i) => (
                <DraggableCard
                  key={s.id}
                  stakeholder={s}
                  onDrop={handleDrop}
                  index={i}
                />
              ))
            )}
          </div>
        </div>

        {/* Right grid */}
        <div className="md:w-[64%] w-full grid grid-cols-2 gap-4">
          {Object.entries(columns)
            .filter(([id]) => id !== "unassigned")
            .map(([id, col]) => (
              <div
                key={id}
                ref={(el) => (dropZones.current[id] = el)}
                className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl overflow-hidden flex flex-col justify-between h-[240px]"
              >
                <div
                  className="text-white text-[14px] font-semibold px-4 py-2"
                  style={{ backgroundColor: col.color }}
                >
                  {col.title}
                </div>
                <div className="flex-1 p-2 text-[14px]">
                  {col.items.length === 0 ? (
                    <p className="text-gray-400 text-sm">
                      Drop stakeholders here...
                    </p>
                  ) : col.items.length <= 2 ? (
                    <div className="flex flex-col gap-y-2">
                      {col.items.map((s, i) => (
                        <DraggableCard
                          key={s.id}
                          stakeholder={s}
                          onDrop={handleDrop}
                          index={i}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 place-items-center">
                      {col.items.map((s, i) => (
                        <CompactDraggableIcon
                          key={s.id}
                          stakeholder={s}
                          onDrop={handleDrop}
                          index={i}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* === LEGEND + CHEVRONS === */}
      <div className="flex items-center justify-between mt-3 text-[13px] text-[#6C757D] leading-snug">
        {/* Legend */}
        <div className="flex flex-col font-gotham font-normal">
          <p>
            * <strong>Influence</strong> = Decision influence on promotion
          </p>
          <p>
            * <strong>Visibility</strong> = Visibility to your outcomes
          </p>
        </div>

        {/* Chevrons */}
        <div className="flex gap-3 ml-6 shrink-0">
          <button
            onClick={onPreviousStep}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F5F5F5] text-[#1C1C1C] shadow-inner hover:bg-[#EAEAEA] transition"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={handleNext}
            disabled={!mapComplete}
            className={`w-9 h-9 flex items-center justify-center rounded-full shadow-inner transition ${
              mapComplete
                ? "bg-[#54B0AF] text-white hover:bg-teal-600"
                : "bg-[#E5E5E5] text-gray-400 cursor-not-allowed"
            }`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</motion.div>


  );
};

export default StakeholderVisibilityMap;
